import omit from 'lodash/omit';

import {
  types as sdkTypes,
  createImageVariantConfig,
} from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';
import {
  getDefaultTimeZoneOnBrowser,
  getStartOf,
  getStartOfWeek,
  monthIdString,
  parseDateFromISO8601,
  stringifyDateToISO8601,
} from '../../util/dates';
import { uniqueBy } from '../../util/generators';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { parse } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';
import axios from 'axios';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  createStripeAccount,
  updateStripeAccount,
  fetchStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import {
  createAlgoliaData,
  deleteImageFromCloudinary,
  deleteMultipleImagesFromCloudinary,
  getCollectionData,
  getListingAvailabilityData,
  singleMailApiCollection,
  updateAlgoliaData,
  updateListingData,
} from '../../util/api';
import { normalizeTaxanomyIcons } from '../../util/taxanomyIcons';
import moment from 'moment';

const { UUID } = sdkTypes;

// Sharetribe Console URL of a listing, per environment. REACT_APP_CONSOLE_URL
// already points at the right marketplace for the current environment
// (.../m/gfhr1-dev/users on dev, gfhr1-test on test, gfhr1 on live), so the
// listing URL is derived from it instead of hardcoding the marketplace id.
const CONSOLE_MARKETPLACE_IDS = {
  production: 'gfhr1',
  test: 'gfhr1-test',
  staging: 'gfhr1-test',
  development: 'gfhr1-dev',
};

const consoleMarketplaceURL = () => {
  const configuredURL = process.env.REACT_APP_CONSOLE_URL;
  if (configuredURL) {
    // Strip the trailing resource path (e.g. '/users') to get the marketplace root.
    return configuredURL.replace(/\/+$/, '').replace(/\/users$/, '');
  }
  const marketplaceId =
    CONSOLE_MARKETPLACE_IDS[process.env.REACT_APP_MODE] ||
    CONSOLE_MARKETPLACE_IDS.development;
  return `https://console.sharetribe.com/o/gfhr1/m/${marketplaceId}`;
};

export const consoleListingURL = listingId =>
  `${consoleMarketplaceURL()}/listings/${listingId}`;


// Create array of N items where indexing starts from 1
const getArrayOfNItems = n =>
  Array(n)
    .fill()
    .map((v, i) => i + 1)
    .slice(1);

// Return an array of image ids
const imageIds = images => {
  // For newly uploaded image the UUID can be found from "img.imageId"
  // and for existing listing images the id is "img.id"
  return images ? images.map(img => img.imageId || img.id) : null;
};

const imageIdString = imageOrId => {
  const imageId = imageOrId?.imageId || imageOrId?.id || imageOrId;
  return imageId?.uuid || imageId;
};

// After listing creation & update, we want to make sure that uploadedImages state is cleaned
const updateUploadedImagesState = (state, payload) => {
  const { uploadedImages, uploadedImagesOrder } = state;

  // Images attached to listing entity
  const attachedImages = payload?.data?.relationships?.images?.data || [];
  const attachedImageUUIDStrings = attachedImages.map(img => img.id.uuid);

  // Uploaded images (which are propably not yet attached to listing)
  const unattachedImages = Object.values(state.uploadedImages);
  const duplicateImageEntities = unattachedImages.filter(unattachedImg =>
    attachedImageUUIDStrings.includes(unattachedImg.imageId?.uuid)
  );
  return duplicateImageEntities.length > 0
    ? {
      uploadedImages: {},
      uploadedImagesOrder: [],
    }
    : {
      uploadedImages,
      uploadedImagesOrder,
    };
};

const getImageVariantInfo = (listingImageConfig = {}) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  const fieldsImage = [
    `variants.${variantPrefix}`,
    `variants.${variantPrefix}-2x`,
  ];

  return {
    fieldsImage,
    imageVariants: {
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    },
  };
};

// `selectedAmenities` is stored grouped by category, e.g.
// { bathroom: [{ icon, id, name }], kitchen: [...] }. Algolia just needs a flat
// list of amenity ids to filter/facet on.
const flattenSelectedAmenities = selectedAmenities => {
  if (!selectedAmenities || typeof selectedAmenities !== 'object') {
    return [];
  }
  return Object.values(selectedAmenities).reduce((allIds, categoryAmenities) => {
    const ids = Array.isArray(categoryAmenities)
      ? categoryAmenities.map(amenity => amenity.id)
      : [];
    return allIds.concat(ids);
  }, []);
};

// `bedroomConfigurations` is an array (one entry per bedroom), each with a
// `beds` map of bed type -> count, e.g. { double: 1, king: 1, ... }. Algolia
// just needs the total number of beds across all bedrooms.
const getTotalBedsCount = bedroomConfigurations => {
  if (!Array.isArray(bedroomConfigurations)) {
    return 0;
  }
  return bedroomConfigurations.reduce((total, configuration) => {
    const beds = configuration?.beds || {};
    const bedsInConfiguration = Object.values(beds).reduce(
      (sum, count) => sum + (Number(count) || 0),
      0
    );
    return total + bedsInConfiguration;
  }, 0);
};

export const getAlgoliaPublicData = publicData => {
  const {
    NumberofBedrooms,
    NumberofBathrooms,
    maximumGuests,
    pets_allowed,
    cloudnaryImage,
    coverPhotoIndex,
    selectedAmenities,
    bedroomConfigurations,
    location
  } = publicData || {};

  return {
    NumberofBedrooms,
    NumberofBathrooms,
    maximumGuests,
    pets_allowed,
    cloudnaryImage,
    coverPhotoIndex,
    amenities: flattenSelectedAmenities(selectedAmenities),
    beds: getTotalBedsCount(bedroomConfigurations),
    location
  };
};

// Availability is stored in Mongo (listing_availbility_data), not in
// publicData.availabilitylot, so the Algolia `date` attribute has to be built
// from the saved rows. Shape expected by search (see util/availability.js):
// [block1Start, block1End, block2Start, block2End, ...] in unix seconds, each
// boundary being the local midnight of a YYYY-MM-DD day.
const availabilityDayToUnix = value => {
  if (!value) {
    return null;
  }

  const dayMatch = String(value).match(/^\d{4}-\d{2}-\d{2}/);
  const day = dayMatch
    ? moment(dayMatch[0], 'YYYY-MM-DD')
    : moment(new Date(value));

  return day.isValid() ? day.unix() : null;
};

const fetchAlgoliaDateArray = listingId => {
  if (!listingId) {
    return Promise.resolve([]);
  }

  return getListingAvailabilityData({ listingId })
    .then(response => {
      const rows = Array.isArray(response?.data) ? response.data : [];

      return rows.reduce((dates, row) => {
        const start = availabilityDayToUnix(row?.start);
        const end = availabilityDayToUnix(row?.end);

        if (start != null && end != null) {
          dates.push(start, end);
        }

        return dates;
      }, []);
    })
    .catch(() => []);
};

const sortExceptionsByStartTime = (a, b) => {
  return a.attributes.start.getTime() - b.attributes.start.getTime();
};

// When navigating through weekly calendar,
// we want to merge new week-related data (inProgres, error) to weeklyExceptionQueries hashmap.
const mergeToWeeklyExceptionQueries = (
  weeklyExceptionQueries,
  weekStartId,
  newDataProps
) => {
  return weekStartId
    ? {
      weeklyExceptionQueries: {
        ...weeklyExceptionQueries,
        [weekStartId]: {
          ...weeklyExceptionQueries[weekStartId],
          ...newDataProps,
        },
      },
    }
    : {};
};
// When navigating through monthly calendar (e.g. when adding a new AvailabilityException),
// we want to merge new month-related data (inProgres, error) to monthlyExceptionQueries hashmap.
const mergeToMonthlyExceptionQueries = (
  monthlyExceptionQueries,
  monthId,
  newDataProps
) => {
  return monthId
    ? {
      monthlyExceptionQueries: {
        ...monthlyExceptionQueries,
        [monthId]: {
          ...monthlyExceptionQueries[monthId],
          ...newDataProps,
        },
      },
    }
    : {};
};

const requestAction = actionType => params => ({
  type: actionType,
  payload: { params },
});

const successAction = actionType => result => ({
  type: actionType,
  payload: result.data,
});

const errorAction = actionType => payload => ({
  type: actionType,
  payload,
  error: true,
});

// ================ Action types ================ //

export const MARK_TAB_UPDATED = 'app/EditListingPage/MARK_TAB_UPDATED';
export const CLEAR_UPDATED_TAB = 'app/EditListingPage/CLEAR_UPDATED_TAB';

export const CREATE_LISTING_DRAFT_REQUEST =
  'app/EditListingPage/CREATE_LISTING_DRAFT_REQUEST';
export const CREATE_LISTING_DRAFT_SUCCESS =
  'app/EditListingPage/CREATE_LISTING_DRAFT_SUCCESS';
export const CREATE_LISTING_DRAFT_ERROR =
  'app/EditListingPage/CREATE_LISTING_DRAFT_ERROR';

export const PUBLISH_LISTING_REQUEST =
  'app/EditListingPage/PUBLISH_LISTING_REQUEST';
export const PUBLISH_LISTING_SUCCESS =
  'app/EditListingPage/PUBLISH_LISTING_SUCCESS';
export const PUBLISH_LISTING_ERROR =
  'app/EditListingPage/PUBLISH_LISTING_ERROR';

export const UPDATE_LISTING_REQUEST =
  'app/EditListingPage/UPDATE_LISTING_REQUEST';
export const UPDATE_LISTING_SUCCESS =
  'app/EditListingPage/UPDATE_LISTING_SUCCESS';
export const UPDATE_LISTING_ERROR = 'app/EditListingPage/UPDATE_LISTING_ERROR';

export const SHOW_LISTINGS_REQUEST =
  'app/EditListingPage/SHOW_LISTINGS_REQUEST';
export const SHOW_LISTINGS_SUCCESS =
  'app/EditListingPage/SHOW_LISTINGS_SUCCESS';
export const SHOW_LISTINGS_ERROR = 'app/EditListingPage/SHOW_LISTINGS_ERROR';

export const FETCH_EXCEPTIONS_REQUEST =
  'app/EditListingPage/FETCH_AVAILABILITY_EXCEPTIONS_REQUEST';
export const FETCH_EXCEPTIONS_SUCCESS =
  'app/EditListingPage/FETCH_AVAILABILITY_EXCEPTIONS_SUCCESS';
export const FETCH_EXCEPTIONS_ERROR =
  'app/EditListingPage/FETCH_AVAILABILITY_EXCEPTIONS_ERROR';
export const FETCH_EXTRA_EXCEPTIONS_SUCCESS =
  'app/EditListingPage/FETCH_EXTRA_AVAILABILITY_EXCEPTIONS_SUCCESS';

export const ADD_EXCEPTION_REQUEST =
  'app/EditListingPage/ADD_AVAILABILITY_EXCEPTION_REQUEST';
export const ADD_EXCEPTION_SUCCESS =
  'app/EditListingPage/ADD_AVAILABILITY_EXCEPTION_SUCCESS';
export const ADD_EXCEPTION_ERROR =
  'app/EditListingPage/ADD_AVAILABILITY_EXCEPTION_ERROR';

export const DELETE_EXCEPTION_REQUEST =
  'app/EditListingPage/DELETE_AVAILABILITY_EXCEPTION_REQUEST';
export const DELETE_EXCEPTION_SUCCESS =
  'app/EditListingPage/DELETE_AVAILABILITY_EXCEPTION_SUCCESS';
export const DELETE_EXCEPTION_ERROR =
  'app/EditListingPage/DELETE_AVAILABILITY_EXCEPTION_ERROR';

export const SET_STOCK_REQUEST = 'app/EditListingPage/SET_STOCK_REQUEST';
export const SET_STOCK_SUCCESS = 'app/EditListingPage/SET_STOCK_SUCCESS';
export const SET_STOCK_ERROR = 'app/EditListingPage/SET_STOCK_ERROR';

export const UPLOAD_IMAGE_REQUEST = 'app/EditListingPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/EditListingPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/EditListingPage/UPLOAD_IMAGE_ERROR';

export const REMOVE_LISTING_IMAGE = 'app/EditListingPage/REMOVE_LISTING_IMAGE';

export const SAVE_PAYOUT_DETAILS_REQUEST =
  'app/EditListingPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS =
  'app/EditListingPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR =
  'app/EditListingPage/SAVE_PAYOUT_DETAILS_ERROR';

export const UPLOAD_VIDEO_REQUEST = 'app/EditListingPage/UPLOAD_VIDEO_REQUEST';
export const UPLOAD_VIDEO_SUCCESS = 'app/EditListingPage/UPLOAD_VIDEO_SUCCESS';
export const UPLOAD_VIDEO_ERROR = 'app/EditListingPage/UPLOAD_VIDEO_ERROR';

export const FETCH_TAXANOMY_ICONS_REQUEST =
  'app/EditListingPage/FETCH_TAXANOMY_ICONS_REQUEST';
export const FETCH_TAXANOMY_ICONS_SUCCESS =
  'app/EditListingPage/FETCH_TAXANOMY_ICONS_SUCCESS';
export const FETCH_TAXANOMY_ICONS_ERROR =
  'app/EditListingPage/FETCH_TAXANOMY_ICONS_ERROR';
// ================ Reducer ================ //

const initialState = {
  // Error instance placeholders for each endpoint
  createListingDraftError: null,
  listingId: null,
  publishListingError: null,
  updateListingError: null,
  showListingsError: null,
  uploadImageError: null,
  setStockError: null,
  setStockInProgress: false,
  createListingDraftInProgress: false,
  submittedListingId: null,
  redirectToListing: false,
  uploadedImages: {},
  uploadedImagesOrder: [],
  removedImageIds: [],
  uploadVideoInProgress: false,
  uploadedVideo: {},
  uploadVideoError: null,
  addExceptionError: null,
  addExceptionInProgress: false,
  weeklyExceptionQueries: {
    // '2022-12-12': { // Note: id/key is the start of the week in given time zone
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: null,
    // },
  },
  monthlyExceptionQueries: {
    // '2022-12': {
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: null,
    // },
  },
  allExceptions: [],
  deleteExceptionError: null,
  deleteExceptionInProgress: false,
  listingDraft: null,
  updatedTab: null,
  updateInProgress: false,
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  taxanomyIcons: [],
  fetchTaxanomyIconsInProgress: false,
  fetchTaxanomyIconsError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case MARK_TAB_UPDATED:
      return { ...state, updatedTab: payload };
    case CLEAR_UPDATED_TAB:
      return { ...state, updatedTab: null, updateListingError: null };

    case CREATE_LISTING_DRAFT_REQUEST:
      return {
        ...state,
        createListingDraftInProgress: true,
        createListingDraftError: null,
        submittedListingId: null,
        listingDraft: null,
      };

    case CREATE_LISTING_DRAFT_SUCCESS:
      return {
        ...state,
        ...updateUploadedImagesState(state, payload),
        createListingDraftInProgress: false,
        submittedListingId: payload.data.id,
        listingDraft: payload.data,
      };
    case CREATE_LISTING_DRAFT_ERROR:
      return {
        ...state,
        createListingDraftInProgress: false,
        createListingDraftError: payload,
      };

    case PUBLISH_LISTING_REQUEST:
      return {
        ...state,
        listingId: payload.listingId,
        publishListingError: null,
      };
    case PUBLISH_LISTING_SUCCESS:
      return {
        ...state,
        redirectToListing: true,
        createListingDraftError: null,
        updateListingError: null,
        showListingsError: null,
        uploadImageError: null,
        createListingDraftInProgress: false,
        updateInProgress: false,
      };
    case PUBLISH_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        publishListingError: {
          listingId: state.listingId,
          error: payload,
        },
      };
    }

    case UPDATE_LISTING_REQUEST:
      return { ...state, updateInProgress: true, updateListingError: null };
    case UPDATE_LISTING_SUCCESS:
      return {
        ...state,
        ...updateUploadedImagesState(state, payload),
        updateInProgress: false,
        // availabilityCalendar: { ...state.availabilityCalendar },
      };
    case UPDATE_LISTING_ERROR:
      return { ...state, updateInProgress: false, updateListingError: payload };

    case SHOW_LISTINGS_REQUEST:
      return { ...state, showListingsError: null };
    case SHOW_LISTINGS_SUCCESS: {
      const listingIdFromPayload = payload.data.id;
      const {
        listingId,
        allExceptions,
        weeklyExceptionQueries,
        monthlyExceptionQueries,
        taxanomyIcons,
        fetchTaxanomyIconsInProgress,
        fetchTaxanomyIconsError,
      } = state;
      const taxanomyIconsState = {
        taxanomyIcons,
        fetchTaxanomyIconsInProgress,
        fetchTaxanomyIconsError,
      };
      // If listing stays the same, we trust previously fetched exception data.
      return listingIdFromPayload?.uuid === state.listingId?.uuid
        ? {
          ...initialState,
          listingId,
          allExceptions,
          weeklyExceptionQueries,
          monthlyExceptionQueries,
          ...taxanomyIconsState,
        }
        : {
          ...initialState,
          listingId: listingIdFromPayload,
          ...taxanomyIconsState,
        };
    }
    case SHOW_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, showListingsError: payload, redirectToListing: false };

    case FETCH_EXCEPTIONS_REQUEST: {
      const { monthId, weekStartId } = payload.params;
      const newData = {
        fetchExceptionsError: null,
        fetchExceptionsInProgress: true,
      };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(
          state.monthlyExceptionQueries,
          monthId,
          newData
        )
        : weekStartId
          ? mergeToWeeklyExceptionQueries(
            state.weeklyExceptionQueries,
            weekStartId,
            newData
          )
          : {};
      return { ...state, ...exceptionQueriesMaybe };
    }
    case FETCH_EXCEPTIONS_SUCCESS: {
      const { exceptions, monthId, weekStartId } = payload;
      const combinedExceptions = state.allExceptions.concat(exceptions);
      const selectId = x => x.id.uuid;
      const allExceptions = uniqueBy(combinedExceptions, selectId).sort(
        sortExceptionsByStartTime
      );
      const newData = { fetchExceptionsInProgress: false };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(
          state.monthlyExceptionQueries,
          monthId,
          newData
        )
        : weekStartId
          ? mergeToWeeklyExceptionQueries(
            state.weeklyExceptionQueries,
            weekStartId,
            newData
          )
          : {};
      return { ...state, allExceptions, ...exceptionQueriesMaybe };
    }
    case FETCH_EXCEPTIONS_ERROR: {
      const { monthId, weekStartId, error } = payload;
      const newData = {
        fetchExceptionsInProgress: false,
        fetchExceptionsError: error,
      };

      const exceptionQueriesMaybe = monthId
        ? mergeToMonthlyExceptionQueries(
          state.monthlyExceptionQueries,
          monthId,
          newData
        )
        : weekStartId
          ? mergeToWeeklyExceptionQueries(
            state.weeklyExceptionQueries,
            weekStartId,
            newData
          )
          : {};

      return { ...state, ...exceptionQueriesMaybe };
    }
    case FETCH_EXTRA_EXCEPTIONS_SUCCESS: {
      const combinedExceptions = state.allExceptions.concat(payload.exceptions);
      const selectId = x => x.id.uuid;
      const allExceptions = uniqueBy(combinedExceptions, selectId).sort(
        sortExceptionsByStartTime
      );
      // TODO: currently we don't handle thrown errors from these paginated calls
      return { ...state, allExceptions };
    }
    case ADD_EXCEPTION_REQUEST:
      return {
        ...state,
        addExceptionError: null,
        addExceptionInProgress: true,
      };
    case ADD_EXCEPTION_SUCCESS: {
      const exception = payload;
      const combinedExceptions = state.allExceptions.concat(exception);
      const allExceptions = combinedExceptions.sort(sortExceptionsByStartTime);
      return {
        ...state,
        allExceptions,
        addExceptionInProgress: false,
      };
    }
    case ADD_EXCEPTION_ERROR:
      return {
        ...state,
        addExceptionError: payload.error,
        addExceptionInProgress: false,
      };

    case DELETE_EXCEPTION_REQUEST:
      return {
        ...state,
        deleteExceptionError: null,
        deleteExceptionInProgress: true,
      };
    case DELETE_EXCEPTION_SUCCESS: {
      const exception = payload;
      const id = exception.id.uuid;
      const allExceptions = state.allExceptions.filter(e => e.id.uuid !== id);
      return {
        ...state,
        allExceptions,
        deleteExceptionInProgress: false,
      };
    }
    case DELETE_EXCEPTION_ERROR:
      return {
        ...state,
        deleteExceptionError: payload.error,
        deleteExceptionInProgress: false,
      };

    case UPLOAD_IMAGE_REQUEST: {
      // payload.params: { id: 'tempId', file }
      const uploadedImages = {
        ...state.uploadedImages,
        [payload.params.id]: { ...payload.params },
      };
      return {
        ...state,
        uploadedImages,
        uploadedImagesOrder: state.uploadedImagesOrder.concat([
          payload.params.id,
        ]),
        uploadImageError: null,
      };
    }
    case UPLOAD_IMAGE_SUCCESS: {
      // payload.params: { id: 'tempId', imageId: 'some-real-id', attributes, type }
      const { id, ...rest } = payload;
      const uploadedImages = { ...state.uploadedImages, [id]: { id, ...rest } };
      return { ...state, uploadedImages };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      const { id, error } = payload;
      const uploadedImagesOrder = state.uploadedImagesOrder.filter(
        i => i !== id
      );
      const uploadedImages = omit(state.uploadedImages, id);
      return {
        ...state,
        uploadedImagesOrder,
        uploadedImages,
        uploadImageError: error,
      };
    }

    case REMOVE_LISTING_IMAGE: {
      const id = imageIdString(payload.imageId);

      // Only mark the image removed if it hasn't been added to the
      // listing already
      const removedImageIds = state.uploadedImages[id]
        ? state.removedImageIds
        : state.removedImageIds.concat(id);

      // Always remove from the draft since it might be a new image to
      // an existing listing.
      const uploadedImages = omit(state.uploadedImages, id);
      const uploadedImagesOrder = state.uploadedImagesOrder.filter(
        i => i !== id
      );

      return { ...state, uploadedImages, uploadedImagesOrder, removedImageIds };
    }

    case SET_STOCK_REQUEST:
      return { ...state, setStockInProgress: true, setStockError: null };
    case SET_STOCK_SUCCESS:
      return { ...state, setStockInProgress: false };
    case SET_STOCK_ERROR:
      return { ...state, setStockInProgress: false, setStockError: payload };

    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return {
        ...state,
        payoutDetailsSaveInProgress: false,
        payoutDetailsSaved: true,
      };
    case UPLOAD_VIDEO_REQUEST:
      return { ...state, uploadVideoInProgress: true };
    case UPLOAD_VIDEO_ERROR:
      return {
        ...state,
        uploadVideoInProgress: false,
        uploadedVideo: null,
        uploadVideoError: payload,
      };
    case UPLOAD_VIDEO_SUCCESS:
      return { ...state, uploadVideoInProgress: false, uploadedVideo: payload };
    case FETCH_TAXANOMY_ICONS_REQUEST:
      return {
        ...state,
        fetchTaxanomyIconsInProgress: true,
        fetchTaxanomyIconsError: null,
      };
    case FETCH_TAXANOMY_ICONS_SUCCESS:
      return {
        ...state,
        fetchTaxanomyIconsInProgress: false,
        taxanomyIcons: payload,
      };
    case FETCH_TAXANOMY_ICONS_ERROR:
      return {
        ...state,
        fetchTaxanomyIconsInProgress: false,
        fetchTaxanomyIconsError: payload,
      };
    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const markTabUpdated = tab => ({
  type: MARK_TAB_UPDATED,
  payload: tab,
});

export const clearUpdatedTab = () => ({
  type: CLEAR_UPDATED_TAB,
});

export const removeListingImage = imageId => ({
  type: REMOVE_LISTING_IMAGE,
  payload: { imageId },
});

// All the action creators that don't have the {Success, Error} suffix
// take the params object that the corresponding SDK endpoint method
// expects.

// SDK method: ownListings.create
export const createListingDraftRequest = requestAction(
  CREATE_LISTING_DRAFT_REQUEST
);
export const createListingDraftSuccess = successAction(
  CREATE_LISTING_DRAFT_SUCCESS
);
export const createListingDraftError = errorAction(CREATE_LISTING_DRAFT_ERROR);

// SDK method: ownListings.publish
export const publishListingRequest = requestAction(PUBLISH_LISTING_REQUEST);
export const publishListingSuccess = successAction(PUBLISH_LISTING_SUCCESS);
export const publishListingError = errorAction(PUBLISH_LISTING_ERROR);

// SDK method: ownListings.update
export const updateListingRequest = requestAction(UPDATE_LISTING_REQUEST);
export const updateListingSuccess = successAction(UPDATE_LISTING_SUCCESS);
export const updateListingError = errorAction(UPDATE_LISTING_ERROR);

// SDK method: ownListings.show
export const showListingsRequest = requestAction(SHOW_LISTINGS_REQUEST);
export const showListingsSuccess = successAction(SHOW_LISTINGS_SUCCESS);
export const showListingsError = errorAction(SHOW_LISTINGS_ERROR);

// SDK method: images.upload
export const uploadImageRequest = requestAction(UPLOAD_IMAGE_REQUEST);
export const uploadImageSuccess = successAction(UPLOAD_IMAGE_SUCCESS);
export const uploadImageError = errorAction(UPLOAD_IMAGE_ERROR);

// SDK method: stock.compareAndSet
export const setStockRequest = requestAction(SET_STOCK_REQUEST);
export const setStockSuccess = successAction(SET_STOCK_SUCCESS);
export const setStockError = errorAction(SET_STOCK_ERROR);

// SDK method: availabilityExceptions.query
export const fetchAvailabilityExceptionsRequest = requestAction(
  FETCH_EXCEPTIONS_REQUEST
);
export const fetchAvailabilityExceptionsSuccess = successAction(
  FETCH_EXCEPTIONS_SUCCESS
);
export const fetchAvailabilityExceptionsError = errorAction(
  FETCH_EXCEPTIONS_ERROR
);
// Add extra data from additional pages
export const fetchExtraAvailabilityExceptionsSuccess = successAction(
  FETCH_EXTRA_EXCEPTIONS_SUCCESS
);

// SDK method: availabilityExceptions.create
export const addAvailabilityExceptionRequest = requestAction(
  ADD_EXCEPTION_REQUEST
);
export const addAvailabilityExceptionSuccess = successAction(
  ADD_EXCEPTION_SUCCESS
);
export const addAvailabilityExceptionError = errorAction(ADD_EXCEPTION_ERROR);

// SDK method: availabilityExceptions.delete
export const deleteAvailabilityExceptionRequest = requestAction(
  DELETE_EXCEPTION_REQUEST
);
export const deleteAvailabilityExceptionSuccess = successAction(
  DELETE_EXCEPTION_SUCCESS
);
export const deleteAvailabilityExceptionError = errorAction(
  DELETE_EXCEPTION_ERROR
);

export const savePayoutDetailsRequest = requestAction(
  SAVE_PAYOUT_DETAILS_REQUEST
);
export const savePayoutDetailsSuccess = successAction(
  SAVE_PAYOUT_DETAILS_SUCCESS
);
export const savePayoutDetailsError = errorAction(SAVE_PAYOUT_DETAILS_ERROR);

// ================ Thunk ================ //

// export const uploadImagesToCloudinary = (file, id, type) => (dispatch, getState, sdk) => {
//   dispatch(uploadImagesRequest(type))
//   if (file && file.name) {
//     const folderNames = type == TAGGED ? tagFolderName : type == DAMAGE ? damageFolderName : type == RECEIPT ? receiptFolderName : folderName;
//     const formData = new FormData();
//     formData.append('file', file);
//     formData.append('folder', folderNames); // Ensure folderName is defined
//     formData.append('public_id', id);
//     // Use Promises instead of async/await
//     return axios
//       .post(`${process.env.REACT_APP_SERVER_MARKETPLACE_ROOT_URL}/api/upload-images`, formData, {
//         headers: {
//           'Content-Type': 'multipart/form-data',
//         },
//       })
//       .then((response) => {
//         // Handle success response
//         dispatch(uploadImagesSuccess(response.data));
//         return response.data;
//       })
//       .catch((error) => {
//         // Handle error response
//         console.error('Error uploading images:', error);
//         dispatch(uploadImagesError(storableError(error)))
//         return null; // Return null or handle as needed
//       });
//   } else {
//     // Return a resolved promise if no file is provided
//     return Promise.resolve(null);
//   }
// };
export const uploadVideoRequest = () => ({
  type: UPLOAD_VIDEO_REQUEST,
});

export const uploadVideoSuccess = response => ({
  type: UPLOAD_VIDEO_SUCCESS,
  payload: response,
});

export const uploadVideoError = error => ({
  type: UPLOAD_VIDEO_ERROR,
  payload: error,
});

export const fetchTaxanomyIconsRequest = () => ({
  type: FETCH_TAXANOMY_ICONS_REQUEST,
});

export const fetchTaxanomyIconsSuccess = icons => ({
  type: FETCH_TAXANOMY_ICONS_SUCCESS,
  payload: icons,
});

export const fetchTaxanomyIconsError = error => ({
  type: FETCH_TAXANOMY_ICONS_ERROR,
  payload: error,
  error: true,
});

export const fetchTaxanomyIcons = () => dispatch => {
  dispatch(fetchTaxanomyIconsRequest());

  return getCollectionData({
    tableName: 'taxanomy_icons',
    query: {},
    options: {},
    fnCallback: false,
  })
    .then(response => {
      const icons = normalizeTaxanomyIcons(response?.data);
      dispatch(fetchTaxanomyIconsSuccess(icons));
      return icons;
    })
    .catch(e => {
      dispatch(fetchTaxanomyIconsError(storableError(e)));
      return [];
    });
};
export const uploadImagesToCloudinary = ({ file }) => (
  dispatch,
  getState,
  sdk
) => {
  try {
    if (file && file.name) {
      const formData = new FormData();
      const uploadURL = process.env.REACT_APP_CLOUDINARY_URL;
      const fileType = file.type.split('/')[0]; // Determines file type from the MIME type (e.g., "image")
      formData.append('file', file);
      formData.append('folder', process.env.REACT_APP_CLOUDINARY_FOLDER_NAME || 'Listing Images');

      formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_PRESET);
      formData.append('resource_type', fileType);
      formData.append('api_key', process.env.REACT_APP_CLOUDINARY_API_KEY);
      return fetch(uploadURL, { method: 'POST', body: formData })
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then(data => {
          return data;
        })
        .catch(error => {
          console.error('Error uploading image:', error);
          throw error;
        });
    }
    return Promise.resolve(true);
  } catch (error) {
    console.error('Unexpected error:', error);
    return Promise.reject(error);
  }
};

export const uploadVideosToCloudinary = (file, id) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(uploadVideoRequest());

  if (file && file.name) {
    const uploadURL = process.env.REACT_APP_CLOUDINARY_URL;
    const formData = new FormData();

    formData.append('file', file);
    formData.append('folder', 'listingVedio'); // folder name
    formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_PRESET);
    formData.append('resource_type', 'video');
    formData.append('api_key', process.env.REACT_APP_CLOUDINARY_API_KEY);

    return fetch(uploadURL, { method: 'POST', body: formData })
      .then(response => {
        if (!response.ok) {
          throw new Error('Cloudinary upload failed');
        }
        return response.json(); // <-- Correct: parse JSON
      })
      .then(data => {
        dispatch(uploadVideoSuccess(data));
        return data;
      })
      .catch(error => {
        dispatch(uploadVideoError(storableError(error)));
        return Promise.reject(error);
      });
  } else {
    return Promise.resolve(null);
  }
};

export const deleteMultipleImages = params => (dispatch, getState, sdk) => {
  return deleteMultipleImagesFromCloudinary({ ...params }).then(res => {
    return res;
  });
};
export const deleteFromCloudinary = (data, listingId, alreadyVedio) => (
  dispatch,
  getState,
  sdk
) => {
  return deleteImageFromCloudinary({
    public_id: data.public_id,
    resource_type: data.resource_type,
  })
    .then(response => {
      if (alreadyVedio) {
        return dispatch(
          updateListingData({
            listingId,
            publicData: {
              listingVideo: null,
            },
          })
        ).then(() => true);
      }
      return true;
    })
    .catch(error => { });
};

// export const deleteImagesFromCloudinary = (data) => (dispatch, getState, sdk) => {
//   return deleteImageFromCloudinary({
//     public_id: data.public_id,
//     resource_type: data.resource_type,
//   })
//     .then((response) => {
//       console.log('Image deleted successfully from Cloudinary');
//       return true;

//     })
//     .catch(error => {
//     });
// };

export function requestShowListing(actionPayload, config) {
  return (dispatch, getState, sdk) => {
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      include: ['author', 'author.profileImage', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    dispatch(showListingsRequest(actionPayload));
    return sdk.ownListings
      .show({ ...actionPayload, ...queryParams })
      .then(response => {
        // EditListingPage fetches new listing data, which also needs to be added to global data
        dispatch(addMarketplaceEntities(response));
        // In case of success, we'll clear state.EditListingPage (user will be redirected away)
        dispatch(showListingsSuccess(response));
        return response;
      })
      .catch(e => dispatch(showListingsError(storableError(e))));
  };
}

// Set stock if requested among listing update info
export function compareAndSetStock(listingId, oldTotal, newTotal) {
  return (dispatch, getState, sdk) => {
    dispatch(setStockRequest());

    return sdk.stock
      .compareAndSet({ listingId, oldTotal, newTotal }, { expand: true })
      .then(response => {
        // NOTE: compareAndSet returns the stock resource of the listing.
        // We update client app's internal state with these updated API entities.
        dispatch(addMarketplaceEntities(response));
        dispatch(setStockSuccess(response));
      })
      .catch(e => {
        log.error(e, 'update-stock-failed', { listingId, oldTotal, newTotal });
        return dispatch(setStockError(storableError(e)));
      });
  };
}

// Helper function to make compareAndSetStock call if stock update is needed.
const updateStockOfListingMaybe = (listingId, stockTotals, dispatch) => {
  const { oldTotal, newTotal } = stockTotals || {};
  // Note: newTotal and oldTotal must be given, but oldTotal can be null
  const hasStockTotals = newTotal >= 0 && typeof oldTotal !== 'undefined';

  if (listingId && hasStockTotals) {
    return dispatch(compareAndSetStock(listingId, oldTotal, newTotal));
  }
  return Promise.resolve();
};

// Create listing in draft state
// NOTE: we want to keep it possible to include stock management field to the first wizard form.
// this means that there needs to be a sequence of calls:
// create, set stock, show listing (to get updated currentStock entity)
export function requestCreateListingDraft(data, config) {
  return (dispatch, getState, sdk) => {
    dispatch(createListingDraftRequest(data));
    const { stockUpdate, images, ...rest } = data;

    // If images should be saved, create array out of the image UUIDs for the API call
    // Note: in this template, image upload is not happening at the same time as listing creation.
    const imageProperty =
      typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    const ownListingValues = { ...imageProperty, ...rest };
    const imageVariantInfo = getImageVariantInfo(
      config?.layout?.listingImage
    );
    const queryParams = {
      expand: true,
      include: ['author', 'author.profileImage', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    let createDraftResponse = null;
    return sdk.ownListings
      .createDraft(ownListingValues, queryParams)
      .then(response => {
        createDraftResponse = response;
        const listingId = response.data.data.id;
        // If stockUpdate info is passed through, update stock
        return updateStockOfListingMaybe(listingId, stockUpdate, dispatch);
      })
      .then(() => {
        // Modify store to understand that we have created listing and can redirect away
        dispatch(createListingDraftSuccess(createDraftResponse));
        return createDraftResponse;
      })
      .catch(e => {
        log.error(e, 'create-listing-draft-failed', { listingData: data });
        return dispatch(createListingDraftError(storableError(e)));
      });
  };
}

// Update the given tab of the wizard with the given data. This saves
// the data to the listing, and marks the tab updated so the UI can
// display the state.
// NOTE: what comes to stock management, this follows the same pattern used in create listing call
export function requestUpdateListing(tab, data, config) {
  return (dispatch, getState, sdk) => {
    dispatch(updateListingRequest(data));
    const { id, stockUpdate, images, ...rest } = data;

    // If images should be saved, create array out of the image UUIDs for the API call
    const imageProperty =
      typeof images !== 'undefined' ? { images: imageIds(images) } : {};
    const ownListingUpdateValues = { id, ...imageProperty, ...rest };
    const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
    const queryParams = {
      expand: true,
      include: ['author', 'author.profileImage', 'images', 'currentStock'],
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    const state = getState();
    const existingTimeZone =
      state.marketplaceData.entities.ownListing[id?.uuid]?.attributes
        ?.availabilityPlan?.timezone;
    const includedTimeZone = rest?.availabilityPlan?.timezone;

    // Note: if update values include stockUpdate, we'll do that first
    // That way we get updated currentStock info among ownListings.update
    return updateStockOfListingMaybe(id, stockUpdate, dispatch)
      .then(() => sdk.ownListings.update(ownListingUpdateValues, queryParams))
      .then(response => {
        dispatch(updateListingSuccess(response));
        dispatch(addMarketplaceEntities(response));
        dispatch(markTabUpdated(tab));
        const { id } = response.data.data || {};
        const {
          state,
          title,
          description,
          publicData,
          geolocation,
          price,
          createdAt,
        } = response.data.data.attributes;
        const algoliaPublicData = getAlgoliaPublicData(publicData);
        const { listingType } = publicData || {};
        const { currentUser } = getState().user;
        const { email } = currentUser.attributes;
        const {
          firstName,
          lastName,
          displayName,
          publicData: user_pub,
        } = currentUser.attributes.profile;
        // const listingId = id?.uuid;
        //   const baseURL = consoleListingURL(listingId);
        //   const SENDGRID_SENDER_EMAIL = process.env.REACT_APP_SENDGRID_REPORT_EMAIL;
        //   const SENDGRID_SENDER_NAME = process.env.REACT_APP_ADMIN_NAME;
        // const payload = {
        //   email: SENDGRID_SENDER_EMAIL,
        //   inviteename: displayName,
        //   name: SENDGRID_SENDER_NAME,
        //   listingname : title,
        //   url: baseURL,
        // };
        // const INVITEE_USER = process.env.REACT_APP_APPRVOED_LISTING_TEMPLATE_ID;
        // singleMailApiCollection({ isCallBackFn: false, template_id: INVITEE_USER, data: payload });
        if (state == 'published') {
          // The availability rows live in the DB, so read them back rather
          // than deriving `date` from publicData - otherwise every listing
          // update would wipe the dates out of the Algolia record.
          fetchAlgoliaDateArray(id?.uuid).then(date =>
            updateAlgoliaData({
              indexName: process.env.REACT_APP_ALGOLIA_LISTINGS_FAMILY_INDEX,
              objectID: id?.uuid,
              _geoloc:
                geolocation && geolocation.lat
                  ? { lat: geolocation.lat, lng: geolocation.lng }
                  : {},
              title,
              description,
              price,
              state,
              date,
              publicData: algoliaPublicData,
              authorData: {
                firstName,
                lastName,
                email,
                user_pub,
                displayName,
                userId: currentUser?.id?.uuid,
              },
              createdAt,
              timestamp: moment(createdAt).unix(),
            })
          );
        }
        // If time zone has changed, we need to fetch exceptions again
        // since week and month boundaries might have changed.
        if (!!includedTimeZone && includedTimeZone !== existingTimeZone) {
          const searchString = '';
          const firstDayOfWeek = config.localization.firstDayOfWeek;
          const listing = response.data.data;
          fetchLoadDataExceptions(
            dispatch,
            listing,
            searchString,
            firstDayOfWeek
          );
        }

        return response;
      })
      .catch(e => {
        log.error(e, 'update-listing-failed', { listingData: data });
        return dispatch(updateListingError(storableError(e)));
      });
  };
}

export const requestPublishListingDraft = listingId => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(publishListingRequest(listingId));

  return sdk.ownListings
    .publishDraft({ id: listingId }, { expand: true })
    .then(response => {
      // Add the created listing to the marketplace data
      dispatch(addMarketplaceEntities(response));
      dispatch(publishListingSuccess(response));
      const listingId = response.data.data.id.uuid;
      const {
        state,
        title,
        description,
        publicData,
        geolocation,
        price,
        createdAt,
      } = response.data.data.attributes;
      const algoliaPublicData = getAlgoliaPublicData(publicData);
      const { listingType } = publicData || {};
      const { currentUser } = getState().user;
      const { email } = currentUser.attributes;
      const {
        firstName,
        lastName,
        displayName,
        publicData: user_pub,
      } = currentUser.attributes.profile;
      // const data = {
      //   publicData: {
      //     listingId,
      //     state,
      //     title
      //   }
      // }
      const baseURL = consoleListingURL(listingId);
      const SENDGRID_SENDER_EMAIL = process.env.REACT_APP_ADMIN_EMAIL;
      const SENDGRID_SENDER_NAME = process.env.REACT_APP_ADMIN_NAME;
      const payload = {
        email: SENDGRID_SENDER_EMAIL,
        inviteename: displayName,
        name: SENDGRID_SENDER_NAME,
        Listingtitle: title,
        url: baseURL,
      };
      const INVITEE_USER = process.env.REACT_APP_APPRVOED_LISTING_TEMPLATE_ID;
      singleMailApiCollection({
        isCallBackFn: false,
        template_id: INVITEE_USER,
        data: payload,
      });
      // Availability comes from the DB rows, and search expects a flat array
      // of unix [start, end] pairs - not the keyed object this used to build
      // out of publicData.availabilitylot.
      return fetchAlgoliaDateArray(listingId)
        .then(date =>
          createAlgoliaData({
            indexName: process.env.REACT_APP_ALGOLIA_LISTINGS_FAMILY_INDEX,
            objectID: listingId,
            _geoloc:
              geolocation && geolocation.lat
                ? { lat: geolocation.lat, lng: geolocation.lng }
                : {},
            title,
            description,
            price,
            state,
            date,
            publicData: algoliaPublicData,
            authorData: {
              firstName,
              lastName,
              email,
              user_pub,
              displayName,
              userId: currentUser?.id?.uuid,
            },
            createdAt,
            timestamp: moment(createdAt).unix(),
          })
        )
        .then(() => response);
    })
    .catch(e => {
      dispatch(publishListingError(storableError(e)));
    });
};

// Images return imageId which we need to map with previously generated temporary id
export function requestImageUpload(actionPayload, listingImageConfig) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    const imageVariantInfo = getImageVariantInfo(listingImageConfig);
    const queryParams = {
      expand: true,
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    dispatch(uploadImageRequest(actionPayload));
    return sdk.images
      .upload({ image: actionPayload.file }, queryParams)
      .then(resp => {
        const img = resp.data.data;
        const uploadedImage = {
          ...img,
          id,
          imageId: img.id,
          file: actionPayload.file,
        };
        // Uploaded image has an existing id that refers to file
        // The UUID was created as a consequence of this upload call - it's saved to imageId property
        dispatch(uploadImageSuccess({ data: uploadedImage }));
        return uploadedImage;
      })
      .catch(e => {
        dispatch(uploadImageError({ id, error: storableError(e) }));
        throw e;
      });
  };
}

export const requestAddAvailabilityException = params => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(addAvailabilityExceptionRequest(params));

  return sdk.availabilityExceptions
    .create(params, { expand: true })
    .then(response => {
      const availabilityException = response.data.data;
      dispatch(
        addAvailabilityExceptionSuccess({ data: availabilityException })
      );
      return availabilityException;
    })
    .catch(e => {
      dispatch(addAvailabilityExceptionError({ error: storableError(e) }));
      throw e;
    });
};

export const requestDeleteAvailabilityException = params => (
  dispatch,
  getState,
  sdk
) => {
  const { id, availabilitylot, listingId } = params;
  dispatch(deleteAvailabilityExceptionRequest({ id }));
  return sdk.availabilityExceptions
    .delete({ id }, { expand: true })
    .then(response => {
      const availabilityException = response.data.data;
      const updatedAvailabilitylot = availabilitylot.filter(
        item => item.id !== id?.uuid
      );
      updateListingData({
        listingId: listingId,
        publicData: {
          availabilitylot: updatedAvailabilitylot,
        },
      });
      return dispatch(
        deleteAvailabilityExceptionSuccess({ data: availabilityException })
      );
    })
    .catch(e => {
      dispatch(deleteAvailabilityExceptionError({ error: storableError(e) }));
      throw e;
    });
};

export const requestFetchAvailabilityExceptions = params => (
  dispatch,
  getState,
  sdk
) => {
  const { listingId, start, end, timeZone, page, isWeekly } = params;
  const fetchParams = { listingId, start, end };
  const timeUnitIdProp = isWeekly
    ? { weekStartId: stringifyDateToISO8601(start) }
    : { monthId: monthIdString(start, timeZone) };
  dispatch(fetchAvailabilityExceptionsRequest(timeUnitIdProp));

  return sdk.availabilityExceptions
    .query(fetchParams)
    .then(response => {
      const availabilityExceptions = denormalisedResponseEntities(response);

      // Fetch potential extra exceptions pagination pages per month.
      // In theory, there could be several pagination pages worth of exceptions,
      // if range is month and unit is 'hour': 31 days * 24 hour = 744 slots for exceptions.
      const totalPages = response.data.meta.totalPages;
      if (totalPages > 1 && !page) {
        const extraPages = getArrayOfNItems(totalPages);

        // It's unlikely that this code is reached with default units.
        // Note:
        //  - Firing multiple API calls might hit API rate limit
        //    (This is very unlikely with this query and 'hour' unit.)
        //  - TODO: this doesn't take care of failures of those extra calls
        Promise.all(
          extraPages.map(page => {
            return sdk.availabilityExceptions.query({ ...fetchParams, page });
          })
        ).then(responses => {
          const denormalizedFlatResults = (all, r) =>
            all.concat(denormalisedResponseEntities(r));
          const exceptions = responses.reduce(denormalizedFlatResults, []);
          dispatch(
            fetchExtraAvailabilityExceptionsSuccess({
              data: { ...timeUnitIdProp, exceptions },
            })
          );
        });
      }

      return dispatch(
        fetchAvailabilityExceptionsSuccess({
          data: { ...timeUnitIdProp, exceptions: availabilityExceptions },
        })
      );
    })
    .catch(e => {
      return dispatch(
        fetchAvailabilityExceptionsError({
          ...timeUnitIdProp,
          error: storableError(e),
        })
      );
    });
};

// Helper function for loadData call.
const fetchLoadDataExceptions = (dispatch, listing, search, firstDayOfWeek) => {
  const hasWindow = typeof window !== 'undefined';
  // Listing could be ownListing entity too, so we just check if attributes key exists
  const hasTimeZone = listing?.attributes?.availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  // Note: listing needs to have time zone set!
  if (hasWindow && listing.id && hasTimeZone) {
    const listingId = listing.id;
    // If the listing doesn't have availabilityPlan yet
    // use the defaul timezone
    const timezone =
      listing.attributes.availabilityPlan?.timezone ||
      getDefaultTimeZoneOnBrowser();
    const todayInListingsTZ = getStartOf(new Date(), 'day', timezone);

    const locationSearch = parse(search);
    const selectedDate = locationSearch?.d
      ? parseDateFromISO8601(locationSearch.d, timezone)
      : todayInListingsTZ;
    const startOfWeek = getStartOfWeek(selectedDate, timezone, firstDayOfWeek);
    const prevWeek = getStartOf(startOfWeek, 'day', timezone, -7, 'days');
    const nextWeek = getStartOf(startOfWeek, 'day', timezone, 7, 'days');
    const nextAfterNextWeek = getStartOf(nextWeek, 'day', timezone, 7, 'days');

    const nextMonth = getStartOf(
      todayInListingsTZ,
      'month',
      timezone,
      1,
      'months'
    );
    const nextAfterNextMonth = getStartOf(
      nextMonth,
      'month',
      timezone,
      1,
      'months'
    );

    const sharedData = { listingId, timeZone: timezone };

    // Fetch data for selected week and nearest weeks for WeeklyCalendar
    // Plus current month and month after that for EditListingAvailabilityForm
    //
    // NOTE: This is making 5 different Thunk calls, which update store 2 times each
    //       It would make sense to make on thunk function that fires 5 sdk calls/promises,
    //       but for the time being, it's clearer to push all the calls through
    //       requestFetchAvailabilityExceptions
    return Promise.all([
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: prevWeek,
          end: startOfWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: startOfWeek,
          end: nextWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          isWeekly: true,
          start: nextWeek,
          end: nextAfterNextWeek,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          start: todayInListingsTZ,
          end: nextMonth,
        })
      ),
      dispatch(
        requestFetchAvailabilityExceptions({
          ...sharedData,
          start: nextMonth,
          end: nextAfterNextMonth,
        })
      ),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

export const savePayoutDetails = (values, isUpdateCall) => (
  dispatch,
  getState,
  sdk
) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  dispatch(savePayoutDetailsRequest());

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      dispatch(savePayoutDetailsSuccess());
      return response;
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};

// loadData is run for each tab of the wizard. When editing an
// existing listing, the listing must be fetched first.
export const loadData = (params, search, config) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(clearUpdatedTab());
  const { id, type } = params;
  const taxanomyIconsPromise = dispatch(fetchTaxanomyIcons());

  if (type === 'new') {
    // No need to listing data when creating a new listing
    return Promise.all([dispatch(fetchCurrentUser()), taxanomyIconsPromise])
      .then(response => {
        const currentUser = getState().user.currentUser;
        if (currentUser && currentUser.stripeAccount) {
          dispatch(fetchStripeAccount());
        }
        return response;
      })
      .catch(e => {
        throw e;
      });
  }

  const payload = { id: new UUID(id) };
  return Promise.all([
    dispatch(requestShowListing(payload, config)),
    dispatch(fetchCurrentUser()),
    taxanomyIconsPromise,
  ])
    .then(response => {
      const currentUser = getState().user.currentUser;
      if (currentUser && currentUser.stripeAccount) {
        dispatch(fetchStripeAccount());
      }

      // Because of two dispatch functions, response is an array.
      // We are only interested in the response from requestShowListing here,
      // so we need to pick the first one
      const listing = response[0]?.data?.data;
      const transactionProcessAlias =
        listing?.attributes?.publicData?.transactionProcessAlias;
      if (listing && isBookingProcessAlias(transactionProcessAlias)) {
        fetchLoadDataExceptions(
          dispatch,
          listing,
          search,
          config.localization.firstDayOfWeek
        );
      }

      return response;
    })
    .catch(e => {
      throw e;
    });
};
